import { useState, useEffect } from 'react';
import { apiRequest } from '../hooks/useAuthStore';
import {
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import clsx from 'clsx';

interface SurveyQuestion {
  id: string;
  question_text: string;
  question_type: 'yes_no' | 'text' | 'textarea' | 'multiple_choice';
  is_required: boolean;
  order_index: number;
  help_text: string | null;
  category: string | null;
  // Followup configuration
  has_followup: boolean;
  followup_trigger: 'yes' | 'no' | 'both' | null;
  followup_question: string | null;
  followup_placeholder: string | null;
  followup_required: boolean;
  followup_min_length: number | null;
  // UI state
  isExpanded?: boolean;
  isNew?: boolean;
}

const CATEGORIES = [
  { value: 'personal', label: 'Información Personal' },
  { value: 'spiritual', label: 'Vida Espiritual' },
  { value: 'family', label: 'Familia' },
  { value: 'trauma', label: 'Trauma/Heridas' },
  { value: 'generational', label: 'Pecado Generacional' },
  { value: 'occult', label: 'Ocultismo' },
  { value: 'forgiveness', label: 'Perdón' },
  { value: 'habits', label: 'Hábitos/Pecados' },
  { value: 'health', label: 'Salud' },
  { value: 'other', label: 'Otro' },
];

export function SurveyEditorPage() {
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<number>(1);

  useEffect(() => {
    fetchQuestions();
  }, [selectedPhase]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    const result = await apiRequest<SurveyQuestion[]>(`/phases/${selectedPhase}/questions`);
    if (result.success && result.data) {
      setQuestions(result.data.map(q => ({ ...q, isExpanded: false })));
    }
    setIsLoading(false);
  };

  const addNewQuestion = () => {
    const newQuestion: SurveyQuestion = {
      id: `new_${Date.now()}`,
      question_text: '',
      question_type: 'yes_no',
      is_required: true,
      order_index: questions.length,
      help_text: null,
      category: 'spiritual',
      has_followup: false,
      followup_trigger: null,
      followup_question: null,
      followup_placeholder: null,
      followup_required: false,
      followup_min_length: null,
      isExpanded: true,
      isNew: true,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (id: string, updates: Partial<SurveyQuestion>) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const deleteQuestion = (id: string) => {
    if (confirm('¿Estás seguro de eliminar esta pregunta?')) {
      setQuestions(questions.filter(q => q.id !== id));
    }
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newQuestions = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    newQuestions.forEach((q, i) => q.order_index = i);
    setQuestions(newQuestions);
  };

  const saveQuestions = async () => {
    // Validate questions
    const invalid = questions.find(q => !q.question_text.trim());
    if (invalid) {
      setSaveMessage({ type: 'error', text: 'Todas las preguntas deben tener texto' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    const result = await apiRequest(`/phases/${selectedPhase}/questions`, {
      method: 'PUT',
      body: JSON.stringify({ questions }),
    });

    if (result.success) {
      setSaveMessage({ type: 'success', text: 'Encuesta guardada exitosamente' });
      fetchQuestions();
    } else {
      setSaveMessage({ type: 'error', text: result.error || 'Error al guardar' });
    }

    setIsSaving(false);
    setTimeout(() => setSaveMessage(null), 5000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Editor de Encuestas</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configura las preguntas que los candidatos responderán en cada fase
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedPhase}
            onChange={(e) => setSelectedPhase(parseInt(e.target.value))}
            className="input w-auto"
          >
            <option value={1}>Fase 1: Valoración</option>
            <option value={2}>Fase 2: Rastreo</option>
            <option value={3}>Fase 3: Ejecución</option>
            <option value={4}>Fase 4: Seguimiento</option>
          </select>
          <button onClick={saveQuestions} disabled={isSaving} className="btn-primary">
            {isSaving ? <span className="spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={clsx(
          'flex items-center gap-2 p-4 rounded-lg',
          saveMessage.type === 'success' ? 'bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400' : 'bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400'
        )}>
          {saveMessage.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {saveMessage.text}
        </div>
      )}

      {/* Instructions */}
      <div className="card p-4 bg-primary-50 dark:bg-primary-500/10 border-primary-200 dark:border-primary-500/30">
        <div className="flex gap-3">
          <HelpCircle className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-primary-700 dark:text-primary-300">
            <p className="font-medium mb-1">Cómo configurar preguntas con detalles adicionales:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Para preguntas de <strong>Sí/No</strong>, puedes activar "Pedir detalles" para solicitar más información</li>
              <li>Configura cuándo pedir detalles: cuando responda "Sí", "No", o en ambos casos</li>
              <li>Ejemplo: "¿Has sufrido abuso?" → Si responde "Sí" → "¿Puedes describir brevemente lo que viviste?"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Questions List */}
      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="spinner mx-auto" />
          <p className="text-gray-500 dark:text-gray-400 mt-4">Cargando preguntas...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              question={question}
              index={index}
              totalQuestions={questions.length}
              onUpdate={(updates) => updateQuestion(question.id, updates)}
              onDelete={() => deleteQuestion(question.id)}
              onMoveUp={() => moveQuestion(index, 'up')}
              onMoveDown={() => moveQuestion(index, 'down')}
            />
          ))}

          {questions.length === 0 && (
            <div className="card p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                No hay preguntas configuradas para esta fase
              </p>
            </div>
          )}

          {/* Add Question Button */}
          <button
            onClick={addNewQuestion}
            className="w-full card p-4 border-2 border-dashed border-gray-300 dark:border-dark-600 hover:border-primary-500 dark:hover:border-primary-500 transition-colors group"
          >
            <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 group-hover:text-primary-500">
              <Plus className="w-5 h-5" />
              <span>Agregar nueva pregunta</span>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// Question Card Component
interface QuestionCardProps {
  question: SurveyQuestion;
  index: number;
  totalQuestions: number;
  onUpdate: (updates: Partial<SurveyQuestion>) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function QuestionCard({ question, index, totalQuestions, onUpdate, onDelete, onMoveUp, onMoveDown }: QuestionCardProps) {
  const isExpanded = question.isExpanded;

  return (
    <div className={clsx('card overflow-hidden', question.isNew && 'ring-2 ring-primary-500')}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-dark-700/50">
        <div className="flex flex-col gap-1">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === totalQuestions - 1}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="badge badge-primary">#{index + 1}</span>
            <span className={clsx('badge', question.question_type === 'yes_no' ? 'badge-success' : 'badge-gray')}>
              {question.question_type === 'yes_no' ? 'Sí/No' : question.question_type === 'textarea' ? 'Texto largo' : 'Texto'}
            </span>
            {question.has_followup && (
              <span className="badge badge-warning">Con detalles</span>
            )}
          </div>
          <p className="mt-1 text-gray-900 dark:text-white font-medium truncate">
            {question.question_text || '(Sin texto)'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdate({ isExpanded: !isExpanded })}
            className="btn-ghost p-2"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          <button onClick={onDelete} className="btn-ghost p-2 text-danger-500 hover:text-danger-600">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-gray-200 dark:border-dark-700">
          {/* Question Text */}
          <div>
            <label className="label">Texto de la pregunta *</label>
            <textarea
              value={question.question_text}
              onChange={(e) => onUpdate({ question_text: e.target.value })}
              className="input"
              rows={2}
              placeholder="Escribe la pregunta aquí..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Question Type */}
            <div>
              <label className="label">Tipo de pregunta</label>
              <select
                value={question.question_type}
                onChange={(e) => onUpdate({ 
                  question_type: e.target.value as SurveyQuestion['question_type'],
                  // Reset followup if not yes_no
                  has_followup: e.target.value === 'yes_no' ? question.has_followup : false,
                })}
                className="input"
              >
                <option value="yes_no">Sí / No</option>
                <option value="text">Texto corto</option>
                <option value="textarea">Texto largo</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="label">Categoría</label>
              <select
                value={question.category || ''}
                onChange={(e) => onUpdate({ category: e.target.value || null })}
                className="input"
              >
                <option value="">Sin categoría</option>
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Required */}
            <div>
              <label className="label">¿Obligatoria?</label>
              <select
                value={question.is_required ? '1' : '0'}
                onChange={(e) => onUpdate({ is_required: e.target.value === '1' })}
                className="input"
              >
                <option value="1">Sí, es obligatoria</option>
                <option value="0">No, es opcional</option>
              </select>
            </div>
          </div>

          {/* Help Text */}
          <div>
            <label className="label">Texto de ayuda (opcional)</label>
            <input
              type="text"
              value={question.help_text || ''}
              onChange={(e) => onUpdate({ help_text: e.target.value || null })}
              className="input"
              placeholder="Ej: Responde honestamente, esta información es confidencial"
            />
          </div>

          {/* Followup Configuration (only for yes_no) */}
          {question.question_type === 'yes_no' && (
            <div className="p-4 bg-gray-50 dark:bg-dark-700/50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Pedir detalles adicionales</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Solicitar más información después de la respuesta Sí/No
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={question.has_followup}
                    onChange={(e) => onUpdate({ 
                      has_followup: e.target.checked,
                      followup_trigger: e.target.checked ? 'yes' : null,
                    })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer dark:bg-dark-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                </label>
              </div>

              {question.has_followup && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-dark-600">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">¿Cuándo pedir detalles?</label>
                      <select
                        value={question.followup_trigger || 'yes'}
                        onChange={(e) => onUpdate({ followup_trigger: e.target.value as 'yes' | 'no' | 'both' })}
                        className="input"
                      >
                        <option value="yes">Solo si responde "Sí"</option>
                        <option value="no">Solo si responde "No"</option>
                        <option value="both">En ambos casos</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">¿El detalle es obligatorio?</label>
                      <select
                        value={question.followup_required ? '1' : '0'}
                        onChange={(e) => onUpdate({ followup_required: e.target.value === '1' })}
                        className="input"
                      >
                        <option value="0">No, es opcional</option>
                        <option value="1">Sí, es obligatorio</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="label">Pregunta de seguimiento</label>
                    <input
                      type="text"
                      value={question.followup_question || ''}
                      onChange={(e) => onUpdate({ followup_question: e.target.value })}
                      className="input"
                      placeholder="Ej: ¿Puedes explicar con más detalle?"
                    />
                  </div>

                  <div>
                    <label className="label">Placeholder del campo (opcional)</label>
                    <input
                      type="text"
                      value={question.followup_placeholder || ''}
                      onChange={(e) => onUpdate({ followup_placeholder: e.target.value || null })}
                      className="input"
                      placeholder="Ej: Describe tu experiencia aquí..."
                    />
                  </div>

                  <div>
                    <label className="label">Longitud mínima de respuesta (caracteres)</label>
                    <input
                      type="number"
                      value={question.followup_min_length || ''}
                      onChange={(e) => onUpdate({ followup_min_length: e.target.value ? parseInt(e.target.value) : null })}
                      className="input w-32"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
